import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import {
  CommentsRepositoryService,
  Comment as PatientComment,
  PatientUploadsRepositoryService,
  Upload,
  CreateReferralSnapshotRequest,
  PracticeDateTimePipe
} from '@xmedical/core';

export interface ReferBackDialogData {
  referralId: number;
  patientId: number;
  patientName: string;
  onSubmit: (referralId: number, request: CreateReferralSnapshotRequest) => Observable<any>;
}

@Component({
  selector: 'xm-refer-back-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    PracticeDateTimePipe,
  ],
  templateUrl: './refer-back-dialog.component.html',
  styleUrls: ['./refer-back-dialog.component.scss']
})
export class ReferBackDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<ReferBackDialogComponent>);
  private dialogData: ReferBackDialogData = inject(MAT_DIALOG_DATA);
  private commentsRepo = inject(CommentsRepositoryService);
  private uploadsRepo = inject(PatientUploadsRepositoryService);
  private snackBar = inject(MatSnackBar);

  isLoading = signal(true);
  isSubmitting = signal(false);

  patientComments = signal<PatientComment[]>([]);
  patientUploads = signal<Upload[]>([]);

  selectedCommentId = signal<number | null>(null);
  selectedUploadIds = signal<Set<number>>(new Set());

  patientName = computed(() => this.dialogData.patientName);
  hasComments = computed(() => this.patientComments().length > 0);
  hasUploads = computed(() => this.patientUploads().length > 0);

  ngOnInit(): void {
    this.loadPatientData();
  }

  onCommentSelected(commentId: number | null): void {
    this.selectedCommentId.set(commentId);
  }

  toggleUpload(uploadId: number, selected: boolean): void {
    this.selectedUploadIds.update(set => {
      const newSet = new Set(set);
      if (selected) { newSet.add(uploadId); } else { newSet.delete(uploadId); }
      return newSet;
    });
  }

  isUploadSelected(uploadId: number): boolean {
    return this.selectedUploadIds().has(uploadId);
  }

  submit(): void {
    if (this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const request: CreateReferralSnapshotRequest = {
      reportingPatientId: this.dialogData.patientId,
      snapshotCommentId: this.selectedCommentId(),
      snapshotUploadIds: Array.from(this.selectedUploadIds())
    };

    this.dialogData.onSubmit(this.dialogData.referralId, request).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.snackBar.open('Snapshot sent successfully', 'Close', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error('Failed to create snapshot:', err);
        this.snackBar.open('Failed to send snapshot', 'Close', { duration: 5000 });
        this.isSubmitting.set(false);
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private loadPatientData(): void {
    this.isLoading.set(true);
    const patientId = this.dialogData.patientId;

    let loaded = 0;
    const checkDone = () => {
      loaded++;
      if (loaded >= 2) {
        this.isLoading.set(false);
      }
    };

    this.commentsRepo.loadPatientComments(patientId, []).subscribe({
      next: (comments) => {
        this.patientComments.set(comments);
        checkDone();
      },
      error: () => {
        this.patientComments.set([]);
        checkDone();
      }
    });

    this.uploadsRepo.loadAll(patientId, []).subscribe({
      next: (uploads) => {
        this.patientUploads.set(uploads);
        checkDone();
      },
      error: () => {
        this.patientUploads.set([]);
        checkDone();
      }
    });
  }
}
